import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useTourStore } from '@/stores/tour';
import { useAuthStore } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { getFilteredSteps } from '@/lib/tour/registry';
import '@/lib/tour'; // registers all tours

const TOUR_ID = 'getting-started';

/**
 * Auto-launches the getting-started tour for first-time users.
 * Only triggers AFTER onboarding is complete (user has an active environment).
 * Uses server-side has_seen_tour as the source of truth so the tour fires
 * only on genuine first sign-in regardless of device or browser state.
 * Filters tour steps based on the user's current role permissions so only
 * pages the user can actually access are shown.
 * Mount once inside PortalLayout.
 */
export function TourLauncher() {
  const { activeTour, completedTours, dismissedTours, startTour } = useTourStore();
  const user = useAuthStore((s) => s.user);
  const markTourSeen = useAuthStore((s) => s.markTourSeen);
  const { can } = usePermissions();
  const location = useLocation();

  // Compute filtered steps outside the effect so can() is a proper dependency.
  // Re-computes whenever the user's permission matrix changes (e.g. role switch).
  const filteredSteps = useMemo(() => getFilteredSteps(TOUR_ID, can), [can]);

  // Sync tour completion/dismissal to the server so it won't re-appear on
  // other devices or after the browser storage is cleared.
  useEffect(() => {
    if (user?.has_seen_tour) {
      return;
    }
    if (completedTours.includes(TOUR_ID) || dismissedTours.includes(TOUR_ID)) {
      void markTourSeen();
    }
  }, [completedTours, dismissedTours, user?.has_seen_tour, markTourSeen]);

  // Auto-launch for users who haven't seen the tour yet.
  useEffect(() => {
    // Don't auto-launch if a tour is already active
    if (activeTour) {
      return;
    }

    // Don't launch during onboarding/setup/auth flows
    const path = location.pathname;
    if (path.includes('/setup') || path.includes('/onboarding') || path.includes('/auth') || path.includes('/login')) {
      return;
    }

    // Don't launch if user hasn't completed onboarding
    if (!user?.active_environment_id) {
      return;
    }

    // Server flag is the source of truth — don't show if already seen
    if (user.has_seen_tour) {
      return;
    }

    // Local store acts as a fast-path cache within the current session
    if (completedTours.includes(TOUR_ID) || dismissedTours.includes(TOUR_ID)) {
      return;
    }

    // Nothing to show — mark as seen silently and bail
    if (filteredSteps.length === 0) {
      void markTourSeen();
      return;
    }

    // Small delay to let the layout render and elements mount
    const timer = setTimeout(() => {
      startTour(TOUR_ID, filteredSteps);
    }, 1500);

    return () => clearTimeout(timer);
  }, [
    location.pathname,
    user?.active_environment_id,
    user?.has_seen_tour,
    filteredSteps,
    activeTour,
    completedTours,
    dismissedTours,
    startTour,
    markTourSeen,
  ]);

  return null;
}
